import { io, Socket } from 'socket.io-client';
import { AdminSocket, Conversation, ConversationSocket } from '~api';
import { logger } from '~portal/lib';

type SocketConfig = {
  /** Backend origin (VITE_BACKEND_API_URL) — socket.io connects on its default path. */
  url: string;
  /** Resolved per connection attempt so reconnects pick up refreshed tokens. */
  getToken: () => Promise<string>;
};

type SocketHandlers = {
  onEvent: (conversationId: string, event: Conversation.ClientEvent<Date, string>) => void;
  onError: (conversationId: string, message: string) => void;
  /** Fired after an automatic reconnect: replay these conversations from the server. */
  onReconnect: (activeConversationIds: string[]) => void;
};

/**
 * @desc Socket payloads cross as JSON; revive the event timestamp into a real Date. Exported for
 * `socket-wire.test.ts`, which round-trips a backend-serialised event through it — the decode
 * half of the wire contract has no other seam to test at.
 */
export const reviveEvent = (
  event: Conversation.ClientEvent<Date, string>,
): Conversation.ClientEvent<Date, string> => ({
  ...event,
  timestamp: new Date(event.timestamp as unknown as string),
});

/**
 * @desc View-independent owner of the conversation event socket (H2-172). One connection per
 * app instance, held open exactly while at least one conversation expects backend updates —
 * navigating between routes never touches it, and state lands in the Zustand store regardless
 * of which view (if any) is mounted. `configure()` is wired once by the SocketBridge inside the
 * ApiProvider tree; everything else is driven by ChatAgent/useConversation.
 */
type ConversationSocketClient = Socket<
  ConversationSocket.ServerToClient & AdminSocket.ServerToClient,
  ConversationSocket.ClientToServer & AdminSocket.ClientToServer
>;

/** @desc The shared socket, typed for every wire contract that rides it (chats and admin rooms). */
export type SharedSocket = ConversationSocketClient;

/**
 * @desc A named, non-conversation consumer of the shared socket. Held by refcount alongside the
 * active conversations, but never handed to `onReconnect` (it is not a conversation to replay):
 * a consumer reconciles itself through {@link SocketManagerImpl.onSocket}.
 */
export type SocketConsumer = 'automations';
const consumers: ReadonlySet<string> = new Set<SocketConsumer>(['automations']);
const isConsumer = (key: string): key is SocketConsumer => consumers.has(key);

/**
 * @desc Called with every socket the manager opens; returns its own teardown, run when that
 * socket is closed (or the listener is removed).
 */
type SocketListener = (socket: SharedSocket) => () => void;

class SocketManagerImpl {
  private socket?: ConversationSocketClient;
  private config?: SocketConfig;
  private handlers?: SocketHandlers;
  /** @desc Conversations currently expecting backend updates — the connection's refcount. */
  private readonly active = new Set<string>();
  /** @desc Named consumers' refcounts (see {@link SocketConsumer}); also hold the connection. */
  private readonly held = new Map<SocketConsumer, number>();
  private readonly listeners = new Set<SocketListener>();
  private readonly teardowns = new Map<SocketListener, () => void>();

  configure(config: SocketConfig, handlers: SocketHandlers) {
    this.config = config;
    this.handlers = handlers;
    // A conversation may have been acquired before configuration landed (first render races).
    if (this.active.size > 0) this.connect();
  }

  /**
   * @desc Mark a conversation as expecting updates — or take a reference on a named consumer
   * (`acquire('automations')`, counted per call) — and open the connection when needed.
   */
  acquire(key: string) {
    if (isConsumer(key)) this.held.set(key, (this.held.get(key) ?? 0) + 1);
    else this.active.add(key);
    this.connect();
  }

  /**
   * @desc A turn finished (or was never running): drop the conversation from the refcount and
   * close the connection once nothing is left expecting updates.
   */
  release(key: string) {
    if (isConsumer(key)) {
      const count = (this.held.get(key) ?? 0) - 1;
      if (count > 0) this.held.set(key, count);
      else this.held.delete(key);
    } else {
      this.active.delete(key);
    }
    if (this.active.size === 0 && this.held.size === 0) this.disconnect();
  }

  /**
   * @desc Observe the shared socket: `listener` runs for the current socket (if any) and for
   * every socket opened after it, and its teardown runs when that socket closes. Returns an
   * unsubscribe that tears down the current attachment. This is how a consumer attaches its own
   * events without the manager knowing them; socket.io's automatic reconnects reuse the same
   * socket (a consumer watches its `connect` event), only a release-to-zero makes a new one.
   */
  onSocket(listener: SocketListener): () => void {
    this.listeners.add(listener);
    if (this.socket) this.teardowns.set(listener, listener(this.socket));
    return () => {
      this.listeners.delete(listener);
      this.teardowns.get(listener)?.();
      this.teardowns.delete(listener);
    };
  }

  /** @desc Re-home a refcount entry once a `new` conversation learns its real id. */
  rekey(from: string, to: string) {
    if (!this.active.has(from)) return;
    this.active.delete(from);
    this.active.add(to);
  }

  /**
   * @desc Ask the server whether a run is in flight for the conversation, holding the
   * connection open while it is. Resolves false (and releases) when nothing is running.
   */
  async watch(conversationId: string): Promise<boolean> {
    this.acquire(conversationId);
    const socket = this.socket;
    if (!socket) {
      this.release(conversationId);
      return false;
    }
    try {
      const { active } = await socket
        .timeout(10_000)
        .emitWithAck('conversation:watch', { conversationId });
      if (!active) this.release(conversationId);
      return active;
    } catch (err) {
      logger.warn('[SocketManager] watch ack failed', { conversationId, error: String(err) });
      this.release(conversationId);
      return false;
    }
  }

  private connect() {
    if (this.socket || !this.config || !this.handlers) return;
    const handlers = this.handlers;

    const socket: ConversationSocketClient = io(this.config.url, {
      withCredentials: true,
      transports: ['websocket'],
      auth: (cb) => {
        this.config!.getToken()
          .then((token) => cb({ token }))
          .catch((err: unknown) => {
            logger.error('[SocketManager] token resolution failed', { error: String(err) });
            cb({});
          });
      },
    });

    socket.on('conversation:event', ({ conversationId, event }) => {
      handlers.onEvent(conversationId, reviveEvent(event));
    });
    socket.on('conversation:error', ({ conversationId, message }) => {
      handlers.onError(conversationId, message);
    });
    socket.on('connect_error', (err) => {
      logger.warn('[SocketManager] connection error', { error: err.message });
    });
    // socket.io reconnects automatically; a gap may have dropped events, so the bridge replays
    // every still-active conversation from the source of truth.
    socket.io.on('reconnect', () => {
      logger.info('[SocketManager] reconnected', { active: this.active.size });
      handlers.onReconnect(Array.from(this.active));
    });

    this.socket = socket;
    for (const listener of this.listeners) this.teardowns.set(listener, listener(socket));
  }

  private disconnect() {
    for (const teardown of this.teardowns.values()) teardown();
    this.teardowns.clear();
    this.socket?.disconnect();
    this.socket = undefined;
  }
}

export const SocketManager = new SocketManagerImpl();
